"use client";

import {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Session, User} from "@supabase/supabase-js";
import {createClient} from "~/utils/supabase/client";
import {useSearchParams} from "next/navigation";

export const dynamic = "force-dynamic";

interface ClientAuthContextProps {
    readonly isUserReady: boolean;
    readonly isSessionRefreshed: boolean;
    readonly isLoggedIn: boolean;
    readonly session: Session | undefined;
    readonly user: User | undefined;
    readonly hasPermUser: boolean;
    readonly hasPermBusiness: boolean;
    readonly hasPermAgent: boolean;
    readonly hasPermAdmin: boolean;
    refreshSession: () => Promise<{}>;
}

const ClientAuthContext = createContext<ClientAuthContextProps>({
    isUserReady: false,
    isSessionRefreshed: false,
    isLoggedIn: false,
    session: undefined,
    user: undefined,
    hasPermUser: false,
    hasPermBusiness: false,
    hasPermAgent: false,
    hasPermAdmin: false,
    refreshSession: async () => ({}),
});

export const EVENTS = {
    SIGNED_IN: "SIGNED_IN",
    SIGNED_OUT: "SIGNED_OUT",
    USER_UPDATED: "USER_UPDATED",
    PASSWORD_RECOVERY: "PASSWORD_RECOVERY",
} as const;

export const ClientAuthProvider = ({ children } : { children: React.ReactNode }) => {

    const searchParams = useSearchParams();
    const supabase = createClient();

    const mounted = useRef(true);

    const [isUserReady, setIsUserReady] = useState<boolean>(false);
    const [isSessionRefreshed, setIsSessionRefreshed] = useState<boolean>(false);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [hasPermUser, setHasPermUser] = useState<boolean>(false);
    const [hasPermBusiness, setHasPermBusiness] = useState<boolean>(false);
    const [hasPermAgent, setHasPermAgent] = useState<boolean>(false);
    const [hasPermAdmin, setHasPermAdmin] = useState<boolean>(false);
    const [session, setSession] = useState<Session | undefined>();
    const [user, setUser] = useState<User | undefined>();

    const setPermsFromUser = (user?: User | null) => {

        const meta = user?.app_metadata as Record<string, unknown> | undefined;

        setHasPermUser(meta?.perm_user === true);
        setHasPermBusiness(meta?.perm_business === true);
        setHasPermAgent(meta?.perm_agent === true);
        setHasPermAdmin(meta?.perm_admin === true);

    };

    useEffect(() => {

        mounted.current = true;

        const getActiveUser = async () => {

            const [{ data: { user }, error }, { data: { session } }] = await Promise.all([
                supabase.auth.getUser(),
                supabase.auth.getSession(),
            ]);

            if (!mounted.current) return;

            if (user && !error) {
                setUser(user);
                setIsLoggedIn(true);
                setPermsFromUser(user);
                if (session) setSession(session);
            } else {
                setIsLoggedIn(false);
                setUser(undefined);
                setPermsFromUser(undefined);
                if (session) setSession(session);
            }

            setIsUserReady(true);
            setIsSessionRefreshed(true);
        };

        getActiveUser();

        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {

            if (!mounted.current) return;

            if (session) {
                setSession(session);
            }

            if (event === EVENTS.SIGNED_IN || event === EVENTS.USER_UPDATED || event === EVENTS.PASSWORD_RECOVERY) {

                const { data: { user } } = await supabase.auth.getUser();

                if (!mounted.current) return;

                if (user) {
                    setUser(user);
                    setIsLoggedIn(true);
                    setPermsFromUser(user);
                }

            } else if (event === EVENTS.SIGNED_OUT) {
                setIsLoggedIn(false);
                setPermsFromUser(undefined);
                setUser(undefined);
            }

        });

        return () => {
            mounted.current = false;
            data.subscription.unsubscribe();
        };

    }, [supabase]);

    const refreshSession = useMemo(
        () => async (): Promise<{}> => {
            const { data: { session } } = await supabase.auth.refreshSession();
            if (mounted.current && session) {
                setSession(session);
            }

            const { data: { user } } = await supabase.auth.getUser();
            if (mounted.current) {
                if (user) {
                    setUser(user);
                    setIsLoggedIn(true);
                    setPermsFromUser(user);
                } else {
                    setIsLoggedIn(false);
                    setPermsFromUser(undefined);
                    setUser(undefined);
                }
            }

            const currentParams = new URLSearchParams(searchParams.toString());
            if (currentParams.has("refreshSession")) {
                currentParams.delete("refreshSession");
                window.location.search = currentParams.toString() ? `?${currentParams.toString()}` : "?";
            } else {
                window.location.reload();
            }

            return {};
        },
        [searchParams, supabase]
    );

    useEffect(() => {
        if (searchParams.has("refreshSession")) {
            refreshSession();
        }
    }, [searchParams, refreshSession]);

    const value = useMemo(() => {
        return {
            isUserReady,
            isSessionRefreshed,
            isLoggedIn,
            session,
            user,
            hasPermUser,
            hasPermBusiness,
            hasPermAgent,
            hasPermAdmin,
            refreshSession,
        };
    }, [isUserReady, isSessionRefreshed, isLoggedIn, session, user, hasPermUser, hasPermBusiness, hasPermAgent, hasPermAdmin, refreshSession]);

    return (
        <ClientAuthContext.Provider value={value}>
            {children}
        </ClientAuthContext.Provider>
    );

}

export const useClientAuth = () => {
    const context = useContext(ClientAuthContext);
    if (context === undefined) {
        throw new Error("useClientAuth must be used within an ClientAuthProvider.");
    }
    return context;
};